import { Droplets, Warehouse, HardHat, KeyRound, ArrowUpRight, ArrowUpToLine } from "lucide-react";
import { Reveal, Overline, IMG, scrollToHash } from "./shared";

const BLOCKS = [
  {
    icon: Warehouse,
    img: "/assets/gallery/rampe-garage-avant-nettoyage-haute-pression.webp",
    alt: "Rampe et murs d'un garage souterrain avant nettoyage haute pression",
    title: "Garages souterrains",
    text: "Nettoyage ponctuel ou périodique : sols, poussières, traces et salissures, zones de circulation, places de stationnement, accès et rampes selon les possibilités techniques.",
  },
  {
    icon: HardHat,
    img: "/assets/gallery/nettoyage-fin-de-chantier-logement-renovation.webp",
    alt: "Logement en fin de rénovation avec gravats, avant nettoyage de fin de chantier",
    title: "Nettoyage fin de chantier",
    text: "Après construction ou rénovation : élimination des poussières, nettoyage des sols, vitres et surfaces, résidus légers, préparation des locaux avant remise.",
  },
  {
    icon: KeyRound,
    img: "/assets/gallery/nettoyage-fin-de-bail-logement-vide-propre.webp",
    alt: "Logement vide, lumineux et parfaitement propre, prêt pour la remise des clés en fin de bail",
    title: "Nettoyage fin de bail",
    text: "Remise en état et nettoyage complet du logement avant sa restitution, pour un départ serein et un bien prêt à accueillir son prochain occupant.",
  },
  {
    icon: ArrowUpToLine,
    img: "/assets/gallery/nettoyage-facade-nacelle-sion.webp",
    alt: "Intervention en nacelle sur la façade vitrée d'un immeuble",
    title: "Hauteur, nacelle & accès difficiles",
    text: "Nettoyage et interventions en hauteur, en nacelle ou encordé : façades vitrées, toitures, zones difficiles d'accès. Nous aimons les défis — et nous pouvons y répondre.",
  },
];

const SURFACES = ["Dalles", "Pavés", "Terrasses", "Entrées d'immeubles", "Murs extérieurs compatibles", "Murets", "Escaliers extérieurs", "Cours & accès", "Sols de garages", "Zones de parkings"];

export const Nettoyages = () => (
  <section id="nettoyages" className="bg-white py-24 lg:py-36" data-testid="nettoyages-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-10">
      <Reveal>
        <Overline>Nettoyages spéciaux</Overline>
        <h2 className="font-display mt-4 max-w-3xl text-3xl font-medium leading-tight tracking-tight text-nuit sm:text-5xl">
          Redonnez un coup de jeune à vos surfaces
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-nuit/65">
          Salissures, poussières, dépôts et marques s'accumulent avec le temps et les intempéries.
          Des interventions ponctuelles permettent de retrouver des surfaces plus propres et plus accueillantes.
        </p>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal className="lg:row-span-2">
          <div className="group relative overflow-hidden">
            <img
              src="/assets/gallery/terrasse-pierre-apres-nettoyage-haute-pression.webp"
              alt="Terrasse en pierre après nettoyage haute pression en Valais"
              loading="lazy"
              className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105 lg:aspect-auto lg:h-full"
            />
            <div className="absolute inset-0 bg-nuit/25" />
            <div className="absolute bottom-0 left-0 bg-nuit p-6 lg:p-8">
              <Droplets className="h-6 w-6 text-lagoon" />
              <h3 className="font-display mt-3 text-2xl font-medium text-white">Nettoyage haute pression</h3>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="text-sm leading-relaxed text-nuit/65 sm:text-base">
            Un nettoyage adapté, selon la nature du support, pour redonner son éclat à chaque surface :
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {SURFACES.map((s) => (
              <span key={s} className="border border-nuit/15 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-petrole">
                {s}
              </span>
            ))}
          </div>
          <p className="font-display mt-8 border-l-2 border-lagoon pl-6 text-lg font-medium leading-snug text-nuit sm:text-xl">
            Parfois, il n'est pas nécessaire de remplacer ou de rénover une surface : un nettoyage professionnel peut déjà transformer son apparence.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 gap-px bg-nuit/10">
          {BLOCKS.map((b, i) => (
            <Reveal key={b.title} delay={0.1 + i * 0.08} className="bg-nuit/10">
              <div className="group flex h-full gap-5 bg-white p-6 transition-colors duration-300 hover:bg-brume lg:p-8">
                <img
                  src={b.img}
                  alt={b.alt}
                  loading="lazy"
                  className="h-24 w-24 shrink-0 object-cover lg:h-28 lg:w-28"
                />
                <div>
                  <h3 className="font-display flex items-center gap-2 text-lg font-medium text-nuit">
                    <b.icon className="h-4 w-4 shrink-0 text-petrole transition-colors duration-300 group-hover:text-lagoon" />
                    {b.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-nuit/60">{b.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="mt-14 grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
        <Reveal>
          <div className="flex aspect-[2/1] overflow-hidden border border-nuit/10" data-testid="nettoyages-avant-apres-preview">
            <div className="relative flex-1 overflow-hidden">
              <img src="/assets/gallery/entretien-mur-pierre-valais.webp" alt="Mur en pierre avant nettoyage haute pression en Valais" loading="lazy" className="h-full w-full object-cover" />
              <span className="absolute left-3 top-3 bg-nuit/85 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-lagoon">Avant</span>
            </div>
            <div className="w-0.5 bg-white" />
            <div className="relative flex-1 overflow-hidden">
              <img src="/assets/gallery/maconnerie-paysagere-pierre-valais.webp" alt="Mur en pierre après nettoyage haute pression en Valais" loading="lazy" className="h-full w-full object-cover" />
              <span className="absolute left-3 top-3 bg-nuit/85 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-lagoon">Après</span>
            </div>
          </div>
          <p className="mt-3 text-xs uppercase tracking-[0.15em] text-nuit/40">Mur en pierre — nettoyage haute pression</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h3 className="font-display text-2xl font-medium tracking-tight text-nuit sm:text-3xl">La preuve par l'image</h3>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-nuit/60 sm:text-base">
            Terrasses, dallages, murs, entrées d'immeubles et garages souterrains : les prochaines
            interventions haute pression seront documentées en avant / après pour montrer concrètement
            la différence.
          </p>
          <a
            href="/realisations"
            data-testid="nettoyages-cta-realisations"
            className="group mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-petrole transition-colors duration-300 hover:text-lagoon"
          >
            Voir les réalisations avant / après
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <div className="mt-16 grid grid-cols-1 items-center gap-6 bg-nuit p-8 lg:grid-cols-2 lg:gap-12 lg:p-10" data-testid="materiel-pro">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-lagoon">Notre équipement</p>
            <p className="font-display mt-3 text-xl font-medium leading-snug text-white sm:text-2xl">
              Du matériel professionnel pour un résultat professionnel
            </p>
          </div>
          <p className="text-sm leading-relaxed text-white/65 sm:text-base">
            Aspirateurs, souffleurs de feuilles, équipements de nettoyage et lavage haute pression :
            nous sommes équipés de machines professionnelles, principalement sur batteries — moins
            de bruit, moins d'émissions, pour le confort des résidents. Nous utilisons également des
            produits de nettoyage les plus naturels possible, respectueux des surfaces et de
            l'environnement, avec le matériel approprié à chaque situation.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
